import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TabItem {
  label: string;
  value: string;
  icon?: React.ReactNode;
  rightAligned?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabClick: (tabValue: string) => void;
  /** Render tabs vertically instead of horizontally */
  vertical?: boolean;
  /** Smaller text and padding */
  compact?: boolean;
  /** Optional badge count per tab */
  badgeCounts?: Record<string, number>;
}

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ tabs, activeTab, onTabClick, vertical = false, compact = false, badgeCounts }, ref) => {
    const { t } = useTranslation();

    if (vertical) {
      return (
        <div ref={ref} className="flex flex-col gap-0.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.value;
            const count = badgeCounts?.[tab.value];
            return (
              <button
                key={tab.value}
                onClick={() => !tab.disabled && onTabClick(tab.value)}
                disabled={tab.disabled}
                title={tab.disabled ? tab.tooltip : ''}
                className={`text-left rounded-md transition-all duration-200 flex items-center gap-2 ${
                  compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
                } font-medium ${
                  isActive
                    ? 'text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
                <span className="flex-1">{t(tab.label)}</span>
                {count != null && count > 0 && (
                  <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div ref={ref} className="overflow-x-auto">
        <div className={`flex ${compact ? 'space-x-3 px-5' : 'space-x-6 px-5'} border-b border-gray-200 dark:border-gray-700 pb-2`}>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.value;
            const count = badgeCounts?.[tab.value];
            const disabledClasses = tab.disabled ? 'opacity-50 cursor-not-allowed' : '';
            const alignmentClasses = tab.rightAligned ? 'ml-auto' : '';

            return (
              <button
                key={tab.value}
                onClick={() => !tab.disabled && onTabClick(tab.value)}
                disabled={tab.disabled}
                title={tab.disabled ? tab.tooltip : ''}
                className={`relative font-medium transition-all duration-300 px-2 py-1 rounded-md flex items-center gap-1.5 ${
                  compact ? 'text-xs' : 'text-sm'
                } ${
                  isActive
                    ? 'text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                } ${disabledClasses} ${alignmentClasses}`}
              >
                {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
                {t(tab.label)}
                {count != null && count > 0 && (
                  <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
);

export default Tabs;
